import { ContentCheck, ContentDelete, ContentLock, NoticeDoNot } from '@fs/zion-icon'
import { Card, Checkbox, Divider, Ellipsis, Image, LabelTooltip, colors, Separator, AudioPlayer } from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PersonGroup } from '@fs/zion-icon/dist/cjs/icons'
import ArtifactActionButtons from './button/ArtifactActionButtons'
import { ARTIFACT_CATEGORY, SCREENING_STATE, SCREENING_TAG } from './util/artifactsConstants'
import { Body2 } from '../app/Title'

const ArtifactCard = ({ artifact, refetchArtifacts, selectable = false, checked, onCheckboxChange }) => {
  const { t } = useTranslation()
  // Handle updates to state internally, without requiring parent to update and pass in new artifact
  const [internalArtifact, setInternalArtifact] = useState(artifact)
  // Also allow external updates, if the parent does re-fetch the artifact
  useEffect(() => setInternalArtifact(artifact), [artifact])

  // Callback from <ArtifactActionButtons>
  const handleActionPerformed = (actionPerformed) => {
    // Clone artifact so that screening state can be updated
    const newArtifact = { ...internalArtifact }
    // Handle the action and update internal artifact state
    switch (actionPerformed) {
      case 'info':
        break
      case 'approve':
        newArtifact.showApproved =
          newArtifact.screeningState === SCREENING_STATE.ESCALATE ||
          newArtifact.screeningState === SCREENING_STATE.RESTRICTED

        newArtifact.screeningState = SCREENING_STATE.APPROVED
        newArtifact.screeningTag = SCREENING_TAG.NOT_SET

        if (refetchArtifacts) {
          setInternalArtifact(newArtifact)
          refetchArtifacts()
        } else {
          setInternalArtifact(newArtifact)
        }
        break
      case 'restrict':
        newArtifact.screeningState = SCREENING_STATE.RESTRICTED
        newArtifact.screeningTag = actionPerformed.pendingScreeningTag
        if (refetchArtifacts) {
          setInternalArtifact(newArtifact)
          refetchArtifacts()
        } else {
          setInternalArtifact(newArtifact)
        }
        break
      case 'delete':
        refetchArtifacts()
        break
      case 'restore':
        newArtifact.screeningState = SCREENING_STATE.RESTORED
        if (refetchArtifacts) {
          setInternalArtifact(newArtifact)
          refetchArtifacts()
        } else {
          setInternalArtifact(newArtifact)
        }
        break
      default:
    }
    // Pass on action, if desired
    // Example: this Card is in ArtifactInfo, which was opened by a parent ArtifactCard. Then the
    // parent ArtifactCard should be able to update if an action was performed in its child ArtifactInfo.
    // if (onActionPerformed) {
    //   onActionPerformed({ action, success, screeningTag })
    // }
  }
  // Create a preview/thumbnail based on the artifact category
  let MediaBlock = null
  let artifactMetaData = null
  if (internalArtifact.screeningState === SCREENING_STATE.RESTRICTED) {
    artifactMetaData = <NoticeDoNot data-testid="restrictedArtifact" size="xs" color={colors.yellow20} />
  }
  if (internalArtifact.screeningState === SCREENING_STATE.APPROVED && internalArtifact.showApproved) {
    artifactMetaData = <ContentCheck data-testid="approvedArtifact" size="xs" color={colors.green30} />
  }
  if (internalArtifact.screeningState === SCREENING_STATE.DELETED) {
    artifactMetaData = <ContentDelete data-testid="deletedArtifact" size="xs" color={colors.red50} />
  }
  if (internalArtifact.visibility === 'PRIVATE') {
    const artifactGroupsLabel =
      artifact.groupIds &&
      t('artifacts.groups', 'Artifact Groups: {groupIds}', {
        groupIds: artifact.groupIds.join(', '),
      })
    artifactMetaData = (
      <span style={{ display: 'block' }}>
        {artifactMetaData && <span style={{ marginInlineStart: '1rem' }} />}
        {artifact.groupIds?.length > 0 ? (
          <span aria-label={artifactGroupsLabel}>
            <LabelTooltip
              target={<PersonGroup id="personGroupIcon" size="xs" color={colors.blue40} />}
              label={artifactGroupsLabel}
            />
          </span>
        ) : (
          <ContentLock data-testid="privateArtifact" size="xs" color={colors.blue90} />
        )}
      </span>
    )
  }
  switch (artifact.category) {
    case ARTIFACT_CATEGORY.AUDIO:
      MediaBlock = <AudioPlayer source={artifact.url} />
      break
    case ARTIFACT_CATEGORY.TEXT:
      MediaBlock = (
        <div>
          <span style={{ fontWeight: 'bold' }}>
            <Ellipsis lineClamp={2}>{artifact.title}</Ellipsis>
          </span>
          <Ellipsis lineClamp={2}>{artifact.description}</Ellipsis>
          <Separator size="xxl" />
        </div>
      )
      break
    case ARTIFACT_CATEGORY.IMAGE:
      MediaBlock =
        artifact.artifactDisplayState === 'PROCESSING' ? (
          <Body2>{t('artifacts.imageIsProcessing', 'Image is processing')}</Body2>
        ) : (
          <Image height="250px" noCrop src={artifact.thumbUrl} alt={artifact.title} />
        )
      break
    case ARTIFACT_CATEGORY.PDF:
    default:
      MediaBlock = <Image height="250px" noCrop src={artifact.thumbUrl} alt={artifact.title} />
  }

  return (
    <div style={{ position: 'relative' }}>
      <Card id="artifactCard">
        {MediaBlock}
        <Ellipsis lineClamp={2}>{artifact.category !== ARTIFACT_CATEGORY.TEXT ? artifact.title : null}</Ellipsis>
        <Ellipsis lineClamp={3}>
          {artifact.category !== ARTIFACT_CATEGORY.TEXT ? (
            <span style={{ display: 'block', marginBottom: artifact.description ? '5px' : undefined }}>
              {artifact.description}
            </span>
          ) : null}
        </Ellipsis>
        {artifactMetaData || <Separator />}
        <Divider />
        <ArtifactActionButtons artifact={internalArtifact} handleActionPerformed={handleActionPerformed} />
      </Card>
      {selectable && (
        <span style={{ position: 'absolute', top: '0', insetInlineEnd: '0' }}>
          <Checkbox
            name="artifactCheckbox"
            checked={checked}
            onChange={(e) => {
              onCheckboxChange(artifact.id, e.target.checked)
            }}
          />
        </span>
      )}
    </div>
  )
}

export default React.memo(ArtifactCard)
